/**
 * A store's FYP profile (sellhiz.win/@<slug>) and its Shop: the addresses, the
 * Shop's stored products and where a product opens. Pure functions: the server,
 * the public pages and the unit tests share them.
 */
import type { FypShopItem, FypShopSource, FypTap, LiveBag, LiveBagItem } from "./types";
import { fypCheckoutUrl } from "./fyp";
import { salesPagePath } from "./liveBag";

/** Products the Shop copies, newest first. */
export const FYP_SHOP_MAX = 100;
/** Videos the profile's grid carries. */
export const FYP_PROFILE_GRID_MAX = 300;
/** A profile's copy older than this is taken again by the hourly refresh. Under
 *  an hour: the run at :00 writes its copies a moment after it starts, so at a
 *  full hour the next :00 found them a few milliseconds too young and left them
 *  for the one after, two hours on. */
export const FYP_PROFILE_STALE_MS = 55 * 60_000;

export type FypAtView =
  | { slug: string; view: "profile" }
  | { slug: string; view: "shop" }
  | { slug: string; view: "reviews" }
  | { slug: string; view: "product"; product: string };

const SLUG = "[a-z0-9]+(?:-[a-z0-9]+)*";
const AT = new RegExp(`^/@(${SLUG})(?:/(shop)(?:/([^/]{1,160}))?|/(ulasan))?/?$`, "i");

/** /@<slug>, /@<slug>/shop, /@<slug>/shop/<product slug> or /@<slug>/ulasan;
 *  anything else is not ours. The product's slug keeps its case: it is looked
 *  up as it is. */
export function parseFypAtPath(pathname: string): FypAtView | null {
  let path: string;
  try {
    path = decodeURIComponent(pathname);
  } catch {
    return null;
  }
  const m = AT.exec(path);
  if (!m || m[1].length > 40) return null;
  const slug = m[1].toLowerCase();
  if (m[3]) return { slug, view: "product", product: m[3] };
  if (m[4]) return { slug, view: "reviews" };
  return { slug, view: m[2] ? "shop" : "profile" };
}

export function fypProfilePath(slug: string): string {
  return `/@${slug}`;
}

export function fypShopPath(slug: string, productSlug?: string | null): string {
  return productSlug ? `/@${slug}/shop/${encodeURIComponent(productSlug)}` : `/@${slug}/shop`;
}

/** The store's "Ulasan pembeli" page, optionally on one star (?bintang=4) or
 *  on the reviews with a photo (?media=1). */
export function fypReviewsPath(slug: string, stars?: number | null, media?: boolean): string {
  if (media) return `/@${slug}/ulasan?media=1`;
  return stars ? `/@${slug}/ulasan?bintang=${stars}` : `/@${slug}/ulasan`;
}

/** The Shop's heading: "Kedai <name>", unless the name already says Kedai. */
export function shopTitle(name: string): string {
  return /^kedai\b/i.test(name.trim()) ? name.trim() : `Kedai ${name.trim()}`;
}

/** A link inside the profile (a Shop product's page), carrying the ad that
 *  brought the viewer, else marked as the profile's (utm_content=profil). */
export function fypShopHref(path: string, slug: string, pageSearch: string): string {
  const url = new URL(path, "https://fyp.invalid");
  const tagged = new URL(fypCheckoutUrl(url.pathname + url.search, slug, null, pageSearch), "https://fyp.invalid");
  return url.pathname + tagged.search;
}

/**
 * Where tapping a post's product goes when the post does not open its checkout:
 * the product's page inside the profile, or its sales page, carrying the video
 * and the ad as the checkout would so the order is still credited to both. Null
 * means the checkout: the post asks for it, or the bag cannot say where else
 * (saved before its products' slugs were kept).
 */
export function fypTapHref(
  tap: FypTap | undefined,
  item: Pick<LiveBagItem, "path" | "slug">,
  slug: string,
  code: string,
  pageSearch: string,
  arm?: "c" | "r" | null,
): string | null {
  const page = tap === "product" ? (item.slug ? fypShopPath(slug, item.slug) : null) : tap === "sales" ? salesPagePath(item.path) : null;
  return page ? fypCheckoutUrl(page, slug, code, pageSearch, arm) : null;
}

const isShopItem = (v: unknown): v is FypShopItem => {
  const i = v as Partial<FypShopItem> | null;
  return (
    !!i &&
    typeof i.productId === "string" &&
    typeof i.slug === "string" &&
    i.slug.length > 0 &&
    !i.slug.includes("/") &&
    typeof i.title === "string" &&
    typeof i.price === "number" &&
    typeof i.currency === "string"
  );
};

const isSource = (v: unknown): v is FypShopSource => {
  const s = v as Partial<FypShopSource> | null;
  return !!s && (s.kind === "sub" || s.kind === "domain" || s.kind === "shop") && typeof s.key === "string" && /^[a-z0-9.-]{1,253}$/i.test(s.key);
};

/** A post's bag with what the Shop's copy knows of each product: its units sold
 *  (the "X terjual" on its video) and its class's start. A product with neither
 *  carries none. */
export function bagWithShop(bag: LiveBag | null, shop: FypShopItem[]): LiveBag | null {
  if (!bag) return bag;
  const known = new Map(
    shop.flatMap((i) =>
      i.sold || i.startsAt ? [[i.productId, { ...(i.sold ? { sold: i.sold } : {}), ...(i.startsAt ? { startsAt: i.startsAt } : {}) }] as const] : [],
    ),
  );
  if (!known.size) return bag;
  return { ...bag, items: bag.items.map((i) => (known.has(i.productId) ? { ...i, ...known.get(i.productId) } : i)) };
}

/** The Shop stored on fyp_links.shop ({source, items}); anything unreadable is left out. */
export function parseShop(raw: string | null | undefined): { source: FypShopSource | null; items: FypShopItem[] } {
  const none = { source: null, items: [] };
  if (!raw) return none;
  let v: { source?: unknown; items?: unknown };
  try {
    v = JSON.parse(raw);
  } catch {
    return none;
  }
  if (!v || typeof v !== "object" || !Array.isArray(v.items)) return none;
  const source = isSource(v.source) ? { kind: v.source.kind, key: v.source.key } : null;
  if (!source) return none;
  const items = v.items
    .filter(isShopItem)
    .slice(0, FYP_SHOP_MAX)
    .map((i) => ({
      productId: i.productId,
      slug: i.slug,
      title: i.title,
      price: i.price,
      currency: i.currency,
      image: typeof i.image === "string" ? i.image : null,
      sold: Number.isInteger(i.sold) && (i.sold as number) > 0 ? (i.sold as number) : 0,
      ...(typeof i.startsAt === "string" && Number.isFinite(Date.parse(i.startsAt)) ? { startsAt: i.startsAt } : {}),
    }));
  return { source, items };
}
